// The feature-request board: five columns, a count in each header, a vote on every card,
// and — for support admins only — a way to move a card between columns
// (docs/superpowers/specs/2026-10-07-feature-requests-design.md).
//
// It renders inside its own Modal; the parent opens it by rendering
// <Board onClose kept onKeptChange />. The Modal's header carries "New request", which
// opens RequestForm in a second Modal stacked over this one; when the server has filed the
// request the form closes and the board loads the list again (GET, through `load`) rather
// than splicing the new row in, so the new card is drawn in New where and as the server
// says it is. A filing whose outcome is unknown (no answer, the form's timeout, a 5xx, a
// 2xx whose answer is not a row) loads the list too, so a request that landed anyway is on
// the board before anyone files it twice; closing the form then keeps its text (`kept`,
// held by the parent so closing the board does not lose it either), and "New request"
// opens it again. The Details panel heads each answer with the form's own labels
// (request-form.js FIELD_LABELS).
// board.js decides everything that can be decided without a DOM — the columns, their
// order, the moves a card offers, how an answer lands in the list, what a refusal says —
// and this file renders and sequences.
//
// MOVING A CARD HAS TWO PATHS, and the drag is the shortcut, not the only one. Each card
// an admin sees carries a "Move to <column>" button for every other column (board.js
// `movesFor`), which a keyboard and a screen reader can use; HTML5 drag and drop (no
// dependency) does the same thing with a mouse. Both call `move`. Buttons, not a <select>
// that commits on change: on Windows an arrow key on a closed select fires `change` on
// every press, so New -> Shipped would have sent Planned and Building as real moves on the
// way. Neither path is drawn for anyone else, which is presentation: PATCH /:id/status is
// refused by rank on the server whatever this shows.
//
// THE SERVER'S ANSWER IS WHAT LANDS. A vote takes the count POST /:id/vote returns, a
// move takes the row PATCH /:id/status returns; nothing is guessed in advance. A 404 (the
// request is gone) or any 403 (a rank or licence refusal, or a stale CSRF token) may mean
// the list on screen is what is wrong, so the board refetches it rather than trusting it;
// `failureMessage` says which refusal it was.
//
// FOCUS STAYS IN THE DIALOG. Focus on <body> is outside the Modal's key handler: Escape
// stops closing it and Ctrl+Enter reaches the page's window listeners. So a busy card's
// controls are aria-disabled, not disabled (disabling the focused button drops focus), and
// `act` refuses the second click instead; and whenever the list redraws with focus lost —
// a moved card drawn in another column, a voted card reordered within its own, a refused
// card gone after the refetch — focus is put back inside the board, on the same card's
// control where that card is still there.
//
// Everything a person typed is rendered as TEXT — React escapes it — never as markup.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { api } from '../../api/client.js';
import { useSession } from '../../session/SessionProvider.jsx';
import Modal from '../Modal.jsx';
import RequestForm from './RequestForm.jsx';
import { FIELD_LABELS } from './request-form.js';
import {
  IMPACT_LABELS,
  STATUS_LABELS,
  applyVote,
  canMoveCards,
  failureMessage,
  groupIntoColumns,
  movesFor,
  replaceRequest,
  shouldRefetch,
} from './board.js';

// A busy control keeps focus and looks unavailable; `act` is what refuses the click.
const BUSY_LOOK = 'aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

function Card({ request, canMove, moves, busy, dragging, onVote, onMove, onDragStart, onDragEnd }) {
  const [open, setOpen] = useState(false);
  const titleId = `feature-request-${request.id}-title`;
  const detailsId = `feature-request-${request.id}-details`;
  const votes = request.vote_count;

  return (
    <li
      draggable={canMove && !busy}
      onDragStart={canMove ? (event) => onDragStart(event, request) : undefined}
      onDragEnd={canMove ? onDragEnd : undefined}
      className={`rounded-md border border-line bg-surface p-3 shadow-sm ${
        canMove ? 'cursor-grab active:cursor-grabbing' : ''
      } ${dragging ? 'opacity-50' : ''}`}
    >
      <h4 id={titleId} className="text-sm font-medium break-words text-ink">{request.title}</h4>
      <p className="mt-1 text-xs text-ink-muted">
        {request.area} · {IMPACT_LABELS[request.impact] ?? request.impact}
        {request.created_by ? ` · ${request.created_by.display_name}` : ''}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-vote={request.id}
          aria-pressed={request.voted_by_me}
          aria-describedby={titleId}
          aria-disabled={busy || undefined}
          onClick={() => onVote(request)}
          className={`btn-ghost px-2 py-1 ring-1 ${BUSY_LOOK} ${
            request.voted_by_me
              ? 'bg-brand-50 text-brand-700 ring-brand-600/40 dark:bg-brand-900 dark:text-brand-100 dark:ring-brand-400/50'
              : 'ring-line-strong'
          }`}
        >
          <span aria-hidden="true">▲</span>
          {votes} {votes === 1 ? 'vote' : 'votes'}
        </button>

        <button
          type="button"
          aria-expanded={open}
          aria-controls={detailsId}
          onClick={() => setOpen((was) => !was)}
          className="btn-ghost px-2 py-1 text-xs"
        >
          {open ? 'Hide details' : 'Details'}
        </button>
      </div>

      {moves.length > 0 && (
        <div data-move-controls={request.id} className="mt-2 flex flex-wrap items-center gap-1.5">
          <span aria-hidden="true" className="text-xs text-ink-secondary">Move to</span>
          {moves.map((status) => (
            <button
              key={status}
              type="button"
              aria-label={`Move to ${STATUS_LABELS[status]}`}
              aria-describedby={titleId}
              aria-disabled={busy || undefined}
              onClick={() => onMove(request, status, { refocus: true })}
              className={`btn-ghost px-2 py-0.5 text-xs ring-1 ring-line-strong ${BUSY_LOOK}`}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>
      )}

      {open && (
        <dl id={detailsId} className="mt-3 space-y-2 border-t border-line pt-2 text-sm">
          <div>
            <dt className="text-xs font-medium text-ink-secondary">{FIELD_LABELS.problem}</dt>
            <dd className="whitespace-pre-wrap break-words text-ink">{request.problem}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-ink-secondary">{FIELD_LABELS.proposal}</dt>
            <dd className="whitespace-pre-wrap break-words text-ink">{request.proposal}</dd>
          </div>
          {request.rules && (
            <div>
              <dt className="text-xs font-medium text-ink-secondary">{FIELD_LABELS.rules}</dt>
              <dd className="whitespace-pre-wrap break-words text-ink">{request.rules}</dd>
            </div>
          )}
        </dl>
      )}
    </li>
  );
}

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {{values: object, message: string}|null} [props.kept]  the form's text kept by a
 *   close after an unknown outcome (RequestForm's `kept`), or null
 * @param {(kept: {values: object, message: string}|null) => void} [props.onKeptChange]
 */
export default function Board({ onClose, kept = null, onKeptChange = () => {} }) {
  const user = useSession();
  const canMove = canMoveCards(user);

  const [requests, setRequests] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const boardRef = useRef(null);
  const refocusId = useRef(null);
  // The card whose vote just landed. A vote can reorder its column (most votes first), and
  // moving the focused card in the DOM drops focus to <body>.
  const votedId = useRef(null);
  // The cards with a vote or move in flight, read synchronously by `act`. `busy` above is
  // the same set for rendering, and lags it by a render.
  const inFlight = useRef(new Set());
  // Only the latest load may land: a refetch after a refused vote must not be
  // overwritten by an older one still in flight.
  const loadSeq = useRef(0);
  // Bumped each time a vote or move lands. A GET that started before one landed may hold
  // that row as it was, so it is asked again rather than allowed to overwrite the newer row.
  const mutationSeq = useRef(0);

  // The error is cleared only by a load that lands, so the "Try again" button holding
  // focus stays mounted while the retry is in flight.
  const load = useCallback(async (signal) => {
    const seq = ++loadSeq.current;
    try {
      for (;;) {
        const mutationsAtStart = mutationSeq.current;
        const body = await api.get('/feature-requests', { signal });
        if (seq !== loadSeq.current) return;
        if (mutationSeq.current === mutationsAtStart) {
          setRequests(body.data);
          setLoadError(null);
          return;
        }
      }
    } catch (err) {
      if (err?.name === 'AbortError' || seq !== loadSeq.current) return;
      setLoadError(err?.message ?? 'The board could not be loaded.');
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // After every redraw of the list, before paint. A card moved with its buttons is drawn
  // again in another column, so the button that had focus is gone: focus the same card's
  // first move button, where the person was. Otherwise, if focus fell to <body>: a vote
  // that reordered its card's column took the pressed vote button out of the DOM and put
  // it back, so focus that card's vote button again; failing that — the focused card
  // vanished in a refetch, or "Try again" gave way to the board — put it on the board. A
  // dialog stacked over this one holds focus itself, so it is never <body> while one is
  // open.
  useLayoutEffect(() => {
    const id = refocusId.current;
    const voted = votedId.current;
    refocusId.current = null;
    votedId.current = null;
    const board = boardRef.current;
    if (!board) return;
    const moved = id === null
      ? null
      : board.querySelector(`[data-move-controls="${CSS.escape(id)}"] button`);
    if (moved) {
      moved.focus();
      return;
    }
    if (document.activeElement !== document.body && document.activeElement !== null) return;
    const voteButton = voted === null ? null : board.querySelector(`[data-vote="${CSS.escape(voted)}"]`);
    (voteButton ?? board).focus();
  }, [requests]);

  function setCardBusy(id, isBusy) {
    setBusy((was) => {
      const next = new Set(was);
      if (isBusy) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function act(request, what, run) {
    // One request in flight per card. The server holds one vote per person regardless
    // (a unique index); this only stops a double-click sending two.
    if (inFlight.current.has(request.id)) return;
    inFlight.current.add(request.id);
    setCardBusy(request.id, true);
    setNotice(null);
    try {
      await run();
    } catch (err) {
      setNotice({ tone: 'error', text: failureMessage(err, what) });
      if (shouldRefetch(err)) load();
    } finally {
      inFlight.current.delete(request.id);
      setCardBusy(request.id, false);
    }
  }

  function vote(request) {
    return act(request, 'Your vote did not count', async () => {
      const body = await api.post(`/feature-requests/${encodeURIComponent(request.id)}/vote`);
      mutationSeq.current += 1;
      votedId.current = request.id;
      setRequests((rows) => applyVote(rows, body.data));
    });
  }

  function move(request, status, { refocus = false } = {}) {
    if (status === request.status) return undefined;
    return act(request, 'The card did not move', async () => {
      const body = await api.patch(
        `/feature-requests/${encodeURIComponent(request.id)}/status`,
        { status },
      );
      mutationSeq.current += 1;
      if (refocus) refocusId.current = body.data.id;
      setRequests((rows) => replaceRequest(rows, body.data));
      setNotice({ tone: 'status', text: `Moved “${body.data.title}” to ${STATUS_LABELS[body.data.status]}.` });
    });
  }

  // The server filed it. Close the form — focus goes back to "New request" — and load the
  // list, which now holds the new card; a load already in flight is superseded by this one.
  function filed(created) {
    setFormOpen(false);
    onKeptChange(null);
    setNotice({ tone: 'status', text: `Filed “${created.title}” in ${STATUS_LABELS[created.status] ?? STATUS_LABELS.new}.` });
    load();
  }

  function handleDragStart(event, request) {
    event.dataTransfer.effectAllowed = 'move';
    // Firefox starts no drag without data set.
    event.dataTransfer.setData('text/plain', request.id);
    setDraggingId(request.id);
  }

  function handleDragEnd() {
    setDraggingId(null);
    setDropTarget(null);
  }

  function columnDropHandlers(status) {
    if (!canMove) return {};
    return {
      onDragOver(event) {
        if (draggingId === null) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (dropTarget !== status) setDropTarget(status);
      },
      onDragLeave(event) {
        // dragleave fires on entering a child too; only leaving the column counts.
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setDropTarget((was) => (was === status ? null : was));
        }
      },
      onDrop(event) {
        event.preventDefault();
        const dragged = requests?.find((r) => r.id === draggingId);
        setDraggingId(null);
        setDropTarget(null);
        if (dragged) move(dragged, status);
      },
    };
  }

  let body;
  if (requests === null && loadError) {
    body = (
      <div className="p-4 text-sm">
        <p className="text-red-700 dark:text-red-300">{loadError}</p>
        <button type="button" className="btn-ghost mt-2 ring-1 ring-line-strong" onClick={() => load()}>
          Try again
        </button>
      </div>
    );
  } else if (requests === null) {
    body = <p className="p-4 text-sm text-ink-muted">Loading…</p>;
  } else {
    const columns = groupIntoColumns(requests);
    body = (
      <div
        ref={boardRef}
        tabIndex={-1}
        className="grid gap-3 p-3 focus:outline-none sm:grid-cols-2 lg:grid-cols-5"
      >
        {columns.map((column) => {
          const headingId = `feature-column-${column.status}`;
          return (
            <section
              key={column.status}
              aria-labelledby={headingId}
              {...columnDropHandlers(column.status)}
              className={`flex min-w-0 flex-col rounded-lg bg-sunken p-2 ${
                dropTarget === column.status ? 'ring-2 ring-brand-600 dark:ring-brand-400' : ''
              }`}
            >
              <h3 id={headingId} className="flex items-baseline justify-between px-1 pb-2 text-sm font-semibold text-ink">
                {column.label}
                <span className="text-xs font-medium text-ink-muted">
                  {column.count}
                  <span className="sr-only">{column.count === 1 ? ' request' : ' requests'}</span>
                </span>
              </h3>
              {column.count === 0 ? (
                <p className="rounded-md border border-dashed border-line-strong px-2 py-6 text-center text-xs text-ink-muted">
                  Nothing here yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {column.requests.map((request) => (
                    <Card
                      key={request.id}
                      request={request}
                      canMove={canMove}
                      moves={movesFor(request.status, user)}
                      busy={busy.has(request.id)}
                      dragging={draggingId === request.id}
                      onVote={vote}
                      onMove={move}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    );
  }

  const newRequest = (
    <button
      type="button"
      className="btn-primary px-2.5 py-1.5"
      onClick={(event) => {
        // The form's Modal returns focus to whatever has it when it opens, and not every
        // browser focuses a button on click (Safari does not).
        event.currentTarget.focus();
        setFormOpen(true);
      }}
    >
      <span aria-hidden="true">+</span>
      New request
    </button>
  );

  return (
    <>
      <Modal title="Feature requests" width="full" onClose={onClose} actions={newRequest}>
        {/* Always mounted, so a screen reader hears what changed. */}
        <div aria-live="polite" className={notice ? 'px-4 pt-3' : 'sr-only'}>
          {notice && (
            <p className={`text-sm ${notice.tone === 'error' ? 'text-red-700 dark:text-red-300' : 'text-ink-secondary'}`}>
              {notice.text}
            </p>
          )}
        </div>
        {requests !== null && loadError && (
          <p className="px-4 pt-3 text-sm text-red-700 dark:text-red-300">
            The board could not be refreshed: {loadError}
          </p>
        )}
        {body}
      </Modal>
      {formOpen && (
        <RequestForm
          kept={kept}
          onClose={(keep) => {
            setFormOpen(false);
            onKeptChange(keep ?? null);
          }}
          onFiled={filed}
          onOutcomeUnknown={() => load()}
        />
      )}
    </>
  );
}
